import React, { useEffect, useState, useMemo } from "react";
import { FaArrowLeft } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import { unwrapString } from "../utils/formatters";
import "../assets/css/ViewCustomer.css";
import "../assets/css/DigixLeads.css";
import { resolveImageUrl } from "../config/apiConfig";
import AddFollowup from "./AddFollowup";
import { SupportSortableHeader } from "./SupportSortableHeader";
import { useResizableColumns } from "../hooks/useResizableColumns";
import TablePagination, { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../components/TablePagination";

const LEAD_FOLLOWUPS_ROWS_PER_PAGE_KEY = "leadFollowupsRowsPerPage";

const fixFileUrl = (url) => {
  if (!url) return url;
  return resolveImageUrl(url);
};

function ViewInternshipLead({ leadId, onBack, onEditLead }) {
  const [loading, setLoading] = useState(true);
  const [lead, setLead] = useState(null);

  // Followups UI state
  const [loadingFollowups, setLoadingFollowups] = useState(false);
  const [followups, setFollowups] = useState([]);
  const [followupSearch, setFollowupSearch] = useState("");
  const [followupDate, setFollowupDate] = useState("");
  const [followupSortColumn, setFollowupSortColumn] = useState("followupDate");
  const [followupSortDirection, setFollowupSortDirection] = useState("desc");
  const [followupPage, setFollowupPage] = useState(1);
  const [followupRowsPerPage, setFollowupRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(LEAD_FOLLOWUPS_ROWS_PER_PAGE_KEY);
      const parsed = parseInt(saved, 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(parsed)) {
        return parsed;
      }
    } catch {
      /* ignore */
    }
    return 5;
  });
  const [showAddFollowupModal, setShowAddFollowupModal] = useState(false);
  const [editingFollowup, setEditingFollowup] = useState(null);
  const [followupEditForm, setFollowupEditForm] = useState({
    followupDate: "",
    followupType: "",
    remarks: "",
    nextFollowupDate: ""
  });
  const [updatingFollowup, setUpdatingFollowup] = useState(false);
  const [deletingFollowup, setDeletingFollowup] = useState(false);
  const followupColDefaultWidths = [170, 130, 260, 170, 170, 140];
  const { getColumnStyle: getFollowupColStyle, handleResizeStart: handleFollowupResizeStart } =
    useResizableColumns(followupColDefaultWidths);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(`/internship-lead/${leadId}`);
        const l =
          response.data?.internship_lead ||
          response.data?.internshipLead ||
          response.data?.internship_lead_data ||
          response.data;
        setLead(l || null);
      } catch (err) {
        console.error("Error fetching internship lead:", err);
        alert("Failed to load internship lead details.");
        setLead(null);
      } finally {
        setLoading(false);
      }
    };

    if (leadId) load();
  }, [leadId]);

  // Fetch followups for this internship lead
  const fetchFollowups = async () => {
    if (!leadId) return;
    setLoadingFollowups(true);
    try {
      const response = await axiosInstance.get(`/internship-followups/${leadId}`);
      const raw = response.data;
      const arr = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.data)
          ? raw.data
          : [];

      setFollowups(arr);
    } catch (err) {
      console.error("Error fetching internship lead followups:", err);
      setFollowups([]);
    } finally {
      setLoadingFollowups(false);
    }
  };

  useEffect(() => {
    if (leadId) fetchFollowups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const formatDateTime = (d) => {
    if (!d) return "—";
    try {
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return "—";
      const day = String(dt.getDate()).padStart(2, "0");
      const month = String(dt.getMonth() + 1).padStart(2, "0");
      const year = dt.getFullYear();
      const hours = String(dt.getHours()).padStart(2, "0");
      const minutes = String(dt.getMinutes()).padStart(2, "0");
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch {
      return "—";
    }
  };

  const filteredFollowups = useMemo(() => {
    return followups.filter((f) => {
      if (followupSearch) {
        const search = followupSearch.toLowerCase();
        const r = (f.Remarks || f.remarks || "").toLowerCase();
        const t = (f.Followup_Type || f.followup_type || "").toLowerCase();
        if (!r.includes(search) && !t.includes(search)) return false;
      }
      if (followupDate) {
        const baseDate = f.Followup_Date || f.followup_date;
        const fDate = baseDate ? new Date(baseDate).toISOString().split("T")[0] : "";
        if (fDate !== followupDate) return false;
      }
      return true;
    });
  }, [followups, followupSearch, followupDate]);

  const getFollowupSortValue = (f, columnId) => {
    if (columnId === "followupDate") {
      const t = new Date(f.Followup_Date || f.followup_date || "").getTime();
      return Number.isFinite(t) ? t : 0;
    }
    if (columnId === "type") return String(f.Followup_Type || f.followup_type || "").toLowerCase();
    if (columnId === "remarks") return String(f.Remarks || f.remarks || "").toLowerCase();
    if (columnId === "nextFollowupDate") {
      const t = new Date(f.Next_Followup_Date || f.next_followup_date || "").getTime();
      return Number.isFinite(t) ? t : 0;
    }
    if (columnId === "createdAt") {
      const t = new Date(f.Created_At || f.Created_On || f.created_at || f.created_on || "").getTime();
      return Number.isFinite(t) ? t : 0;
    }
    return "";
  };

  const sortedFollowups = useMemo(() => {
    if (!followupSortColumn) return filteredFollowups;
    const mult = followupSortDirection === "desc" ? -1 : 1;
    return [...filteredFollowups].sort((a, b) => {
      const va = getFollowupSortValue(a, followupSortColumn);
      const vb = getFollowupSortValue(b, followupSortColumn);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      const ida = Number(a?.Followup_Id ?? a?.followup_id ?? a?.id ?? 0);
      const idb = Number(b?.Followup_Id ?? b?.followup_id ?? b?.id ?? 0);
      return (ida - idb) * mult;
    });
  }, [filteredFollowups, followupSortColumn, followupSortDirection]);

  const followupTotalPages = Math.max(1, Math.ceil(sortedFollowups.length / followupRowsPerPage));
  const followupStartIdx = (followupPage - 1) * followupRowsPerPage;
  const currentFollowups = sortedFollowups.slice(followupStartIdx, followupStartIdx + followupRowsPerPage);

  useEffect(() => {
    if (followupPage > followupTotalPages && followupTotalPages > 0) {
      setFollowupPage(1);
    }
  }, [followupPage, followupTotalPages, sortedFollowups.length]);

  const handleFollowupRowsPerPageChange = (next) => {
    setFollowupRowsPerPage(next);
    setFollowupPage(1);
    localStorage.setItem(LEAD_FOLLOWUPS_ROWS_PER_PAGE_KEY, String(next));
  };

  const handleFollowupSort = (columnId) => {
    setFollowupPage(1);
    if (followupSortColumn === columnId) {
      setFollowupSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setFollowupSortColumn(columnId);
      setFollowupSortDirection("asc");
    }
  };

  const toDateTimeLocal = (value) => {
    if (!value) return "";
    // Accept "YYYY-MM-DD HH:mm:ss" or ISO and convert to datetime-local "YYYY-MM-DDTHH:mm"
    const s = String(value);
    const normalized = s.includes("T") ? s : s.replace(" ", "T");
    const date = new Date(normalized);
    if (isNaN(date.getTime())) return "";
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const hh = String(date.getHours()).padStart(2, "0");
    const mi = String(date.getMinutes()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  };

  const toBackendDateTime = (localValue) => {
    if (!localValue) return null;
    const s = String(localValue);
    // datetime-local: "YYYY-MM-DDTHH:mm" -> "YYYY-MM-DD HH:mm:ss"
    const hasSeconds = /:\d{2}:\d{2}$/.test(s);
    const withSeconds = hasSeconds ? s : `${s}:00`;
    return withSeconds.replace("T", " ");
  };

  const handleCloseAddFollowupModal = () => {
    setShowAddFollowupModal(false);
    fetchFollowups();
  };

  const handleEditFollowup = (followup) => {
    if (!followup || typeof followup !== "object") return;
    setEditingFollowup(followup);
    setFollowupEditForm({
      followupDate: toDateTimeLocal(followup.Followup_Date || followup.followup_date),
      followupType: followup.Followup_Type || followup.followup_type || "",
      remarks: followup.Remarks || followup.remarks || "",
      nextFollowupDate: toDateTimeLocal(followup.Next_Followup_Date || followup.next_followup_date)
    });
  };

  const handleCloseEditFollowup = () => {
    setEditingFollowup(null);
    setFollowupEditForm({ followupDate: "", followupType: "", remarks: "", nextFollowupDate: "" });
  };

  const handleFollowupEditChange = (e) => {
    const { name, value } = e.target;
    setFollowupEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateFollowupSubmit = async (e) => {
    e.preventDefault();
    if (!editingFollowup) return;

    const followupId =
      editingFollowup.Followup_Id ?? editingFollowup.followup_id ?? editingFollowup.id;
    if (!followupId) return;

    setUpdatingFollowup(true);
    try {
      let updatedBy = 1;
      try {
        const userStr = localStorage.getItem("user");
        if (userStr) {
          const user = JSON.parse(userStr);
          updatedBy = user.user_id || user.id || 1;
        }
      } catch {
        // ignore
      }

      await axiosInstance.put(`/update-internship-followup/${followupId}`, {
        Followup_Date: toBackendDateTime(followupEditForm.followupDate),
        Followup_Id: followupId,
        Followup_Type: followupEditForm.followupType || null,
        Internship_Lead_Id: leadId,
        Remarks: followupEditForm.remarks || null,
        Next_Followup_Date: toBackendDateTime(followupEditForm.nextFollowupDate),
        Updated_By: updatedBy
      });

      alert("Followup updated successfully.");
      handleCloseEditFollowup();
      fetchFollowups();
    } catch (err) {
      console.error("Error updating internship followup:", err);
      alert(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to update followup."
      );
    } finally {
      setUpdatingFollowup(false);
    }
  };

  const handleDeleteFollowup = async (followupId) => {
    if (!followupId) return;
    if (!window.confirm("Are you sure you want to delete this followup?")) return;

    setDeletingFollowup(true);
    try {
      let response;
      try {
        response = await axiosInstance.delete(`/internship-followup/${followupId}`);
      } catch (err) {
        const status = err?.response?.status;
        // Some backends expose deletion via GET; try fallback if DELETE isn't allowed.
        if (status === 405) {
          response = await axiosInstance.get(`/internship-followup/${followupId}`);
        } else {
          throw err;
        }
      }

      const msg =
        response.data?.message ||
        response.data?.error ||
        "Internship followup deleted successfully.";
      alert(msg);
      fetchFollowups();
    } catch (err) {
      console.error("Error deleting internship followup:", err);
      alert(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to delete followup."
      );
    } finally {
      setDeletingFollowup(false);
    }
  };

  if (loading) {
    return (
      <div className="viewlead-container">
        <div>Loading internship lead details...</div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="viewlead-container">
        <div>Internship lead not found.</div>
        <button className="viewlead-back-link" onClick={onBack}>
          <FaArrowLeft /> Back
        </button>
      </div>
    );
  }

  const clean = (x) => {
    const unwrapped = unwrapString(x);
    const s = unwrapped == null ? "" : String(unwrapped).trim();
    // Some backend fields come with mismatched quotes, e.g. "\"4545436'"
    // Strip leading/trailing quotes/backslashes to avoid showing garbage characters.
    return s.replace(/^[\\'"`]+|[\\'"`]+$/g, "");
  };
  const address = lead.address || {};
  const resumeUrl = fixFileUrl(lead?.Resume_Url);
  const resumePath = lead?.Resume_Path || "";

  const InfoItem = ({ label, value }) => {
    const hasValue = value != null && value !== "" && value !== "—";
    return (
      <div className="viewlead-info-item">
        <span className={`viewlead-info-label ${hasValue ? "has-value" : ""}`}>{label}: </span>
        <span className={`viewlead-info-value ${!hasValue ? "muted" : ""}`}>{hasValue ? value : "—"}</span>
      </div>
    );
  };

  return (
    <div className="viewlead-container">
      <div className="viewlead-header">
        <button className="viewlead-back-link" onClick={onBack}>
          <FaArrowLeft /> Back
        </button>
        {onEditLead && (
          <button
            className="btn-edit-lead"
            onClick={() => onEditLead(leadId, "internship-lead")}
          >
            Edit Internship Lead
          </button>
        )}
      </div>

      <div className="viewlead-card">
        <h3 className="viewlead-section-title">Personal Information</h3>
        <div className="viewlead-info-grid">
          <InfoItem label="Internship Lead ID" value={lead.Internship_Lead_Id ?? lead.internship_lead_id ?? ""} />
          <InfoItem label="First Name" value={clean(lead.First_Name)} />
          <InfoItem label="Last Name" value={clean(lead.Last_Name)} />
          <InfoItem label="Email" value={clean(lead.Email)} />
          <InfoItem label="Phone" value={clean(lead.Phone)} />
          <InfoItem label="Country Code" value={clean(lead.Country_Code)} />
          <InfoItem label="Alt Phone" value={clean(lead.Alt_Phone)} />
        </div>

        <h3 className="viewlead-section-title">Internship Details</h3>
        <div className="viewlead-info-grid">
          <InfoItem label="Qualification" value={lead.Qualification_Name || lead.qualification_name || lead.Qualification_Id || ""} />
          <InfoItem label="Available From" value={clean(lead.Available_From)} />
          <InfoItem label="College Name" value={clean(lead.College_Name)} />
          <InfoItem label="Designation" value={lead.Designation_Name || lead.designation_name || lead.Designation_Id || ""} />
          <InfoItem label="Year Of Passing" value={clean(lead.Year_Of_Passing)} />
          <InfoItem label="Internship Duration" value={clean(lead.Internship_Duration)} />
          <InfoItem label="Skills" value={clean(lead.Skills)} />
          <InfoItem label="Preferred Location" value={clean(lead.Preferred_Location)} />
        </div>

        <h3 className="viewlead-section-title">Status & Source</h3>
        <div className="viewlead-info-grid">
          <InfoItem label="Source" value={lead.Source_Name || lead.source_name || lead.Source_Id || ""} />
          <InfoItem label="Status" value={lead.Status || lead.Status_Id || ""} />
        </div>

        <h3 className="viewlead-section-title">Address</h3>
        <div className="viewlead-info-grid">
          <InfoItem label="Line 1" value={clean(address.Address_Line_1)} />
          <InfoItem label="Line 2" value={clean(address.Address_Line_2)} />
          <InfoItem label="City" value={address.City_Name || address.City_Id || ""} />
          <InfoItem label="State" value={address.State_Name || address.State_Id || ""} />
          <InfoItem label="Country" value={address.Country_Name || address.Country_Id || ""} />
          <InfoItem label="Zip Code" value={clean(address.Zip_Code)} />
        </div>

        <h3 className="viewlead-section-title">Resume</h3>
        <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
          {resumeUrl ? (
            <a className="viewlead-info-value viewlead-link" href={resumeUrl} target="_blank" rel="noreferrer">
              View Resume
            </a>
          ) : (
            <span className="muted-text">—</span>
          )}
          {!resumeUrl && resumePath ? <span style={{ color: "#7A8CA5", fontStyle: "italic" }}>{resumePath}</span> : null}
        </div>
      </div>

      {/* Followups Section */}
      <div className="viewlead-section">
        <div className="viewlead-section-header">
          <h3>Followups</h3>
          <div className="viewlead-section-actions">
            <input
              type="text"
              className="viewlead-search-input"
              placeholder="Search followups by remarks..."
              value={followupSearch}
              onChange={(e) => {
                setFollowupSearch(e.target.value);
                setFollowupPage(1);
              }}
            />
            <input
              type="date"
              className="viewlead-date-input"
              value={followupDate}
              onChange={(e) => {
                setFollowupDate(e.target.value);
                setFollowupPage(1);
              }}
            />
            <button
              className="btn-add-followup"
              onClick={() => setShowAddFollowupModal(true)}
              title="New Followup"
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              New Followup
            </button>
          </div>
        </div>

        {loadingFollowups ? (
          <p className="viewlead-no-data">Loading followups...</p>
        ) : sortedFollowups.length > 0 ? (
          <>
          <table className="viewlead-table viewlead-followups-table">
            <thead>
              <tr>
                <th className="viewlead-th-sortable" style={getFollowupColStyle(0)}>
                  <div className="viewlead-th-sort-row viewlead-th-sort-row--center">
                    <SupportSortableHeader columnId="followupDate" label="Followup Date" sortColumn={followupSortColumn} sortDirection={followupSortDirection} onSort={handleFollowupSort} />
                  </div>
                  <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleFollowupResizeStart(0, e); }} />
                </th>
                <th className="viewlead-th-sortable" style={getFollowupColStyle(1)}>
                  <div className="viewlead-th-sort-row viewlead-th-sort-row--center">
                    <SupportSortableHeader columnId="type" label="Type" sortColumn={followupSortColumn} sortDirection={followupSortDirection} onSort={handleFollowupSort} />
                  </div>
                  <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleFollowupResizeStart(1, e); }} />
                </th>
                <th className="viewlead-th-sortable" style={getFollowupColStyle(2)}>
                  <div className="viewlead-th-sort-row viewlead-th-sort-row--center">
                    <SupportSortableHeader columnId="remarks" label="Remarks" sortColumn={followupSortColumn} sortDirection={followupSortDirection} onSort={handleFollowupSort} />
                  </div>
                  <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleFollowupResizeStart(2, e); }} />
                </th>
                <th className="viewlead-th-sortable" style={getFollowupColStyle(3)}>
                  <div className="viewlead-th-sort-row viewlead-th-sort-row--center">
                    <SupportSortableHeader columnId="nextFollowupDate" label="Next Followup Date" sortColumn={followupSortColumn} sortDirection={followupSortDirection} onSort={handleFollowupSort} />
                  </div>
                  <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleFollowupResizeStart(3, e); }} />
                </th>
                <th className="viewlead-th-sortable" style={getFollowupColStyle(4)}>
                  <div className="viewlead-th-sort-row viewlead-th-sort-row--center">
                    <SupportSortableHeader columnId="createdAt" label="Created At" sortColumn={followupSortColumn} sortDirection={followupSortDirection} onSort={handleFollowupSort} />
                  </div>
                  <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleFollowupResizeStart(4, e); }} />
                </th>
                <th style={getFollowupColStyle(5)}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentFollowups.map((f, idx) => {
                const followupId = f.Followup_Id || f.followup_id || f.id || idx;
                return (
                  <tr key={followupId || idx}>
                    <td>{formatDateTime(f.Followup_Date || f.followup_date)}</td>
                    <td>{f.Followup_Type || f.followup_type || "—"}</td>
                    <td>{f.Remarks || f.remarks || "—"}</td>
                    <td className={!f.Next_Followup_Date && !f.next_followup_date ? "muted-cell" : ""}>
                      {f.Next_Followup_Date || f.next_followup_date
                        ? formatDateTime(f.Next_Followup_Date || f.next_followup_date)
                        : "—"}
                    </td>
                    <td className="blue-cell">
                      {formatDateTime(f.Created_At || f.Created_On || f.created_at || f.created_on)}
                    </td>
                    <td>
                      <div className="viewlead-followup-actions">
                        <button
                          type="button"
                          className="btn-edit-followup"
                          onClick={() => handleEditFollowup(f)}
                          disabled={updatingFollowup || deletingFollowup}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn-delete-followup"
                          onClick={() => handleDeleteFollowup(followupId)}
                          disabled={updatingFollowup || deletingFollowup}
                          title="Delete"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <TablePagination
            currentPage={followupPage}
            totalItems={sortedFollowups.length}
            rowsPerPage={followupRowsPerPage}
            rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
            onPageChange={setFollowupPage}
            onRowsPerPageChange={handleFollowupRowsPerPageChange}
            itemLabel="followups"
            rowsPerPageSelectId="internship-lead-followups-rows-per-page"
          />
          </>
        ) : (
          <p className="viewlead-no-data">No followups found.</p>
        )}
      </div>

      {showAddFollowupModal && (
        <div
          className="viewlead-modal-overlay viewlead-addfollowup-overlay"
          onClick={() => setShowAddFollowupModal(false)}
        >
          <div
            className="viewlead-modal-content viewlead-addfollowup-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="viewlead-modal-header">
              <h3 className="viewlead-modal-title">New Followup</h3>
              <button
                type="button"
                className="viewlead-modal-close"
                onClick={() => setShowAddFollowupModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="viewlead-addfollowup-modal-body">
              <AddFollowup
                id={leadId}
                type="internship-lead"
                onBack={handleCloseAddFollowupModal}
              />
            </div>
          </div>
        </div>
      )}

      {editingFollowup && (
        <div
          className="viewlead-modal-overlay"
          onClick={() => !updatingFollowup && handleCloseEditFollowup()}
        >
          <div
            className="viewlead-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="viewlead-modal-header">
              <h3 className="viewlead-modal-title">Edit Followup</h3>
              <button
                type="button"
                className="viewlead-modal-close"
                onClick={handleCloseEditFollowup}
                disabled={updatingFollowup}
              >
                &times;
              </button>
            </div>
            <div className="viewlead-editfollowup-modal-body">
              <form onSubmit={handleUpdateFollowupSubmit}>
                <div className="form-grid">
                  <div className="form-group">
                    <label>
                      Followup Date <span className="required">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      name="followupDate"
                      value={followupEditForm.followupDate}
                      onChange={handleFollowupEditChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Followup Type</label>
                    <input
                      type="text"
                      name="followupType"
                      value={followupEditForm.followupType}
                      onChange={handleFollowupEditChange}
                      placeholder="e.g., Call, Meeting, WhatsApp"
                    />
                  </div>
                  <div className="form-group">
                    <label>Next Followup Date</label>
                    <input
                      type="datetime-local"
                      name="nextFollowupDate"
                      value={followupEditForm.nextFollowupDate}
                      onChange={handleFollowupEditChange}
                    />
                  </div>
                  <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                    <label>Remarks</label>
                    <textarea
                      name="remarks"
                      value={followupEditForm.remarks}
                      onChange={handleFollowupEditChange}
                      rows={4}
                    />
                  </div>
                </div>
                <div className="form-actions">
                  <button type="button" className="btn-cancel" onClick={handleCloseEditFollowup}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-add-lead" disabled={updatingFollowup}>
                    {updatingFollowup ? "Updating..." : "Update Followup"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ViewInternshipLead;


